import { Dispatch, forwardRef, RefObject, SetStateAction } from "react";

import { useTranslate } from "@/providers/context";

import { useCustomPlayer } from "../../hooks/useCustomPlayer";
import { VolumeSlider } from "./VolumeSlider";
import { TimeDisplay } from "./TimeDisplay";
import { PlayButton } from "./PlayButton";
import { Scrubber } from "./Scrubber";

interface CustomAudioPlayerProps {
  playing: boolean;
  setPlaying: Dispatch<SetStateAction<boolean>>;
  autoPlay?: boolean;
}

export const CustomAudioPlayer = forwardRef<
  HTMLVideoElement,
  CustomAudioPlayerProps
>(({ autoPlay, playing, setPlaying }, ref) => {
  const playerRef = ref as RefObject<HTMLVideoElement>;
  const {
    togglePlay,
    toggleMute,
    handleVolumeChange,
    handleTimeUpdate,
    handleProgress,
    handleLoadedMetadata,
    handleSeek,
    handleScrub,
    handleSkipBack,
    handleSkipForward,
    volume,
    muted,
    currentTime,
    duration,
    progressPercentage,
    bufferedPercentage,
  } = useCustomPlayer({
    playerRef,
    playing,
    setPlaying,
    autoPlay,
  });

  const { translate } = useTranslate();

  return (
    <div className="flex h-[90px] w-full items-center rounded-2xl bg-item-secondary px-9 text-grey50">
      <video
        ref={ref}
        className="hidden"
        onTimeUpdate={handleTimeUpdate}
        onProgress={handleProgress}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setPlaying(false)}
        autoPlay={autoPlay}
        onCanPlay={() => {
          if (autoPlay) {
            togglePlay(true);
          }
        }}
      >
        {translate("Your browser does not support this video format.")}
      </video>
      <div className="mr-5 flex items-center justify-between gap-5">
        <PlayButton
          playing={playing}
          togglePlay={togglePlay}
          progressPercentage={progressPercentage}
          large
        />
        <TimeDisplay
          duration={duration}
          currentTime={currentTime}
          handleSkipBack={handleSkipBack}
          handleSkipForward={handleSkipForward}
        />
      </div>
      <Scrubber
        handleSeek={handleSeek}
        handleScrub={handleScrub}
        progressPercentage={progressPercentage}
        bufferedPercentage={bufferedPercentage}
        isAudio
      />
      <VolumeSlider
        className="ml-5"
        volume={volume}
        handleVolumeChange={handleVolumeChange}
        muted={muted}
        toggleMute={toggleMute}
      />
    </div>
  );
});

CustomAudioPlayer.displayName = "CustomAudioPlayer";
